import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const UPDATED = "3. september 2026";

const PAGES = {
  privacy: {
    path: "/privatliv",
    title: "Privatlivspolitik | SpørgMig",
    heading: "Privatlivspolitik",
    intro: "Her kan du se hvilke personoplysninger SpørgMig behandler, hvorfor vi bruger dem og hvilke rettigheder du har.",
    sections: [
      {
        title: "1. Dataansvarlig og kontakt",
        paragraphs: [
          <>SpørgMig er dataansvarlig for behandlingen beskrevet på denne side. Spørgsmål om persondata kan sendes til <Mail />.</>,
        ],
      },
      {
        title: "2. Oplysninger vi behandler",
        bullets: [
          "Kontooplysninger som navn, e-mailadresse, rolle og krypterede loginoplysninger.",
          "Ekspertoplysninger som profilfoto, beskrivelse, erfaring, priser, specialer og indsendt dokumentation.",
          "Forespørgsler, beskeder, billeder, videoer, forslag, bookinger og anmeldelser som brugerne opretter.",
          "Tekniske oplysninger som IP-adresse, enhedstype, sessions-id, henvisende side og hændelser på platformen.",
          "Supporthenvendelser og oplysninger der er nødvendige for at undersøge misbrug eller sikkerhedsproblemer.",
        ],
      },
      {
        title: "3. Formål og behandlingsgrundlag",
        paragraphs: [
          "Vi behandler oplysninger for at oprette og administrere konti, vise ekspertprofiler, formidle kontakt, drive konsultationsforløb, sende servicemails og yde support.",
          "Behandlingen sker når den er nødvendig for at levere den tjeneste brugeren beder om, for at overholde en retlig forpligtelse eller ud fra vores legitime interesse i sikker drift, fejlfinding, misbrugsforebyggelse og forbedring af SpørgMig. Samtykke bruges kun hvor reglerne kræver det og kan trækkes tilbage.",
        ],
      },
      {
        title: "4. Offentlige oplysninger",
        paragraphs: [
          "Godkendte ekspertprofiler er offentlige. Det gælder blandt andet navn, profilfoto, faglig beskrivelse, specialer, priser og anmeldelser. Kundeprofiler er ikke offentlige som standard.",
          "Del ikke CPR-numre, adgangskoder, kortoplysninger eller andre følsomme oplysninger i profiler, forespørgsler eller beskeder.",
        ],
      },
      {
        title: "5. Leverandører og modtagere",
        paragraphs: [
          "Vi bruger leverandører til hosting, database, fillagring, e-mail og drift. De omfatter på nuværende tidspunkt Cloudflare, Railway, MongoDB Atlas og Resend. Oplysninger deles kun når det er nødvendigt for at levere deres funktion.",
          "Nogle leverandører kan behandle oplysninger uden for EU/EØS. I de tilfælde skal overførslen være baseret på et gyldigt overførselsgrundlag og relevante sikkerhedsforanstaltninger.",
        ],
      },
      {
        title: "6. Opbevaring og sletning",
        paragraphs: [
          "Vi opbevarer kun oplysninger så længe de er nødvendige for formålet. Når en konto slettes, fjernes konto og profil fra de aktive brugerflader. Enkelte oplysninger kan opbevares længere hvis det er nødvendigt for sikkerhed, dokumentation af aftaler, konflikter eller lovkrav.",
          "Sikkerhedskopier og tekniske logs slettes efter de almindelige slettecyklusser hos de relevante leverandører. Oplysninger kan anonymiseres og bruges til statistik uden at kunne knyttes til en bestemt person.",
        ],
      },
      {
        title: "7. Dine rettigheder",
        paragraphs: [
          "Du kan blandt andet bede om indsigt, rettelse, sletning, begrænsning eller udlevering af dine oplysninger. Du kan også gøre indsigelse mod visse behandlinger. Rettighederne afhænger af behandlingens grundlag og gældende undtagelser.",
          <>Kontakt <Mail /> for at bruge dine rettigheder. Du kan klage til Datatilsynet på <External href="https://www.datatilsynet.dk">datatilsynet.dk</External>.</>,
        ],
      },
      {
        title: "8. Sikkerhed og ændringer",
        paragraphs: [
          "Vi bruger tekniske og organisatoriske tiltag til at beskytte oplysninger mod tab, misbrug og uautoriseret adgang. Ingen internetbaseret tjeneste kan dog garantere fuldstændig sikkerhed.",
          "Politikken opdateres når platformen, leverandørerne eller behandlingen ændrer sig. Datoen øverst viser den seneste version.",
        ],
      },
    ],
  },
  terms: {
    path: "/vilkaar",
    title: "Brugervilkår | SpørgMig",
    heading: "Brugervilkår",
    intro: "Disse vilkår gælder for brug af SpørgMig og beskriver platformens rolle samt rammerne mellem kunder og eksperter.",
    sections: [
      {
        title: "1. Om SpørgMig",
        paragraphs: [
          "SpørgMig er en digital platform der hjælper kunder med at finde eksperter og giver parterne værktøjer til forespørgsler, forslag, beskeder og online konsultationer.",
          <>Spørgsmål om vilkårene kan sendes til <Mail />.</>,
        ],
      },
      {
        title: "2. Platformens rolle",
        paragraphs: [
          "SpørgMig formidler kontakten. Eksperten leverer sin egen tid og faglige vurdering. Når en kunde accepterer et forslag, indgås aftalen om konsultationen direkte mellem kunden og eksperten.",
          "SpørgMig er ikke arbejdsgiver for eksperterne og er ikke part i den faglige rådgivning. En godkendt profil betyder at profilen er gennemgået. Det er ikke en garanti for et bestemt resultat.",
        ],
      },
      {
        title: "3. Konti og alder",
        bullets: [
          "Brugere skal være mindst 18 år eller have gyldig tilladelse fra en forælder eller værge.",
          "Oplysninger skal være korrekte og holdes opdaterede.",
          "Kontoen er personlig. Login må ikke deles med andre.",
          "SpørgMig kan begrænse eller lukke konti ved svig, misbrug, chikane, ulovligt indhold eller alvorlige brud på vilkårene.",
        ],
      },
      {
        title: "4. Ekspertens ansvar",
        bullets: [
          "Eksperten skal beskrive erfaring og kvalifikationer sandfærdigt.",
          "Eksperten må kun påtage sig opgaver som vedkommende har de nødvendige kompetencer og eventuelle tilladelser til.",
          "Eksperten er selv ansvarlig for skat, moms og andre forpligtelser knyttet til betaling fra kunden.",
          "Eksperten skal gøre det tydeligt hvis en vurdering er usikker eller kræver fysisk undersøgelse eller en autoriseret fagperson.",
        ],
      },
      {
        title: "5. Kundens ansvar",
        bullets: [
          "Kunden skal give de oplysninger der er nødvendige for at forstå problemet.",
          "Kunden skal kontrollere forslagets pris, omfang og tidspunkt før det accepteres.",
          "Kunden må ikke dele adgangskoder, kortoplysninger, CPR-numre eller unødvendige følsomme oplysninger.",
          "Kunden er ansvarlig for hvordan rådgivningen anvendes og skal søge akut eller autoriseret hjælp når situationen kræver det.",
        ],
      },
      {
        title: "6. Betaling",
        paragraphs: [
          "SpørgMig modtager, reserverer eller videreformidler ikke betaling og opkræver ikke platformsgebyr. Betaling aftales og gennemføres direkte mellem kunden og eksperten.",
          "Pris, betalingsmetode og tidspunkt skal aftales tydeligt før konsultationen begynder. Den part der modtager betalingen er ansvarlig for relevant kvittering og skatte- eller momsbehandling.",
        ],
      },
      {
        title: "7. Aflysning og uenighed",
        paragraphs: [
          "En åben forespørgsel kan slettes før en aftale er indgået. Når en aftale er aktiv, kan både kunden og eksperten aflyse den fra opgaven.",
          "Hvis kunden aflyser mindst 3 timer før det aftalte tidspunkt, har kunden krav på 100% tilbage. Præcis 3 timer regnes som rettidig aflysning.",
          "Hvis kunden aflyser mindre end 3 timer før, har kunden krav på 50% tilbage. Eksperten beholder de resterende 50%.",
          "Hvis eksperten aflyser, har kunden altid krav på 100% tilbage. En sen aflysning fra eksperten registreres af SpørgMig.",
          "Når betalingen er sket direkte mellem kunden og eksperten, skal en tilbagebetaling også gennemføres direkte mellem dem. SpørgMig registrerer udfaldet men flytter ikke pengene.",
          "Ved uenighed kan kunden eller eksperten oprette en supportsag fra opgaven. SpørgMig kan gennemgå aftalen, dialogen, vedhæftningerne og den registrerede betalingsstatus.",
          <>Hvis parterne ikke kan finde en løsning, kan de kontakte <Mail />. SpørgMig kan hjælpe med at skabe overblik over forløbet, men kan ikke træffe en bindende juridisk afgørelse mellem parterne.</>,
        ],
      },
      {
        title: "8. Anmeldelser",
        paragraphs: [
          "Anmeldelser skal bygge på et reelt gennemført forløb og beskrive brugerens egen oplevelse. Falske, truende, diskriminerende eller uvedkommende anmeldelser kan fjernes.",
          "En ekspert kan kontakte support hvis en anmeldelse efter ekspertens vurdering bryder reglerne.",
        ],
      },
      {
        title: "9. Uacceptabel brug",
        bullets: [
          "Ulovlige opgaver, svig, chikane, trusler og diskrimination er ikke tilladt.",
          "Brugere må ikke offentliggøre materiale de ikke har ret til at dele.",
          "Platformen må ikke bruges til spam, automatiseret scraping, skadelig kode eller forsøg på at omgå sikkerheden.",
        ],
      },
      {
        title: "10. Ansvar og drift",
        paragraphs: [
          "SpørgMig forsøger at holde platformen tilgængelig og sikker, men funktioner kan blive ændret eller midlertidigt utilgængelige. Fejl skal meldes til support.",
          "Intet i vilkårene begrænser rettigheder eller ansvar som ikke lovligt kan fraviges. Platformens ansvar vurderes efter dansk ret.",
        ],
      },
      {
        title: "11. Ændringer",
        paragraphs: [
          "Vilkårene kan blive opdateret når SpørgMig eller de gældende regler ændrer sig. Væsentlige ændringer bliver gjort tydelige på platformen eller sendt til berørte brugere. Den gældende version fremgår af datoen øverst.",
        ],
      },
    ],
  },
  cookies: {
    path: "/cookies",
    title: "Cookiepolitik | SpørgMig",
    heading: "Cookies og lokal lagring",
    intro: "SpørgMig bruger cookies og browserlagring til login, sikker drift og statistik over hvordan tjenesten bliver brugt.",
    sections: [
      {
        title: "1. Nødvendig lagring",
        paragraphs: [
          "Der gemmes en login-token i lokal lagring når en bruger logger ind. Dit valg om statistikcookies gemmes også så platformen kan huske det.",
          "Loginlagringen er nødvendig for at levere den funktion du beder om. Sletter du den i browseren, kan du blive logget ud. Du kan altid åbne cookieindstillingerne igen fra footeren.",
        ],
      },
      {
        title: "2. Statistikcookies",
        paragraphs: [
          "Hvis du tillader cookies til statistik, registrerer SpørgMig begrænsede oplysninger om besøg og centrale handlinger. Det kan være side, henvisende side, kampagneparametre, enhedstype og et tilfældigt sessions-id. Formålet er at finde fejl og forbedre tjenesten.",
          "Analysedata må ikke indeholde beskedtekster, adgangskoder eller indholdet af billeder og videoer.",
        ],
      },
      {
        title: "3. Google Analytics",
        paragraphs: [
          "Google Analytics bliver kun indlæst hvis funktionen er konfigureret og du har givet samtykke til statistikcookies. Den er ikke nødvendig for at bruge SpørgMig.",
          "Du kan altid ændre dit valg gennem Cookieindstillinger i footeren eller under dine kontoindstillinger.",
        ],
      },
      {
        title: "Google Ads og måling af annoncer",
        paragraphs: [
          "Google Ads indlæses kun når funktionen er konfigureret og du særskilt tillader måling af annoncer i cookieindstillingerne. Eksisterende samtykke til statistik giver ikke automatisk samtykke til Google Ads.",
          "Målingen forbinder et annonceklik med en indsendt forespørgsel. Google modtager et teknisk forespørgsels-id til at undgå dobbelttælling. Vi sender ikke din problemtekst, dit navn eller din e-mail med denne konvertering. Annoncepersonalisering er slået fra i integrationen.",
        ],
      },
      {
        title: "4. Ændringer og kontakt",
        paragraphs: [
          <>Politikken opdateres hvis vi tilføjer nye statistik- eller marketingværktøjer. Spørgsmål kan sendes til <Mail />.</>,
        ],
      },
    ],
  },
  contact: {
    path: "/kontakt",
    title: "Kontakt og klager | SpørgMig",
    heading: "Kontakt og klager",
    intro: "Har du brug for hjælp til en konto, forespørgsel, ekspert eller konsultation, kan du kontakte SpørgMig support.",
    sections: [
      {
        title: "Kontakt support",
        paragraphs: [
          <>Skriv til <Mail /> og beskriv kort hvad henvendelsen handler om. Oplys gerne forespørgsels- eller bookingnummer. Send aldrig adgangskoder eller kortoplysninger.</>,
          "Support forsøger at svare hurtigst muligt. Komplicerede sager kan kræve oplysninger fra både kunden og eksperten.",
        ],
      },
      {
        title: "Klage over et forløb",
        bullets: [
          "Kontakt først den anden part gennem beskedsystemet og forsøg at finde en løsning.",
          "Gem aftaler og relevante beskeder på platformen.",
          "Skriv til support hvis problemet ikke bliver løst.",
          "Beskriv hvad der blev aftalt, hvad der skete og hvilken løsning du ønsker.",
        ],
      },
      {
        title: "Ulovligt eller skadeligt indhold",
        paragraphs: [
          <>Indhold der virker ulovligt, truende, diskriminerende eller svigagtigt skal anmeldes til <Mail /> med link eller anden identifikation af indholdet. Ved akut fare skal du kontakte de relevante myndigheder.</>,
        ],
      },
      {
        title: "Persondata",
        paragraphs: [
          <>Anmodninger om indsigt, rettelse eller sletning kan også sendes til <Mail />. Se <Link className="font-semibold text-brand-fresh underline underline-offset-4" to="/privatliv">privatlivspolitikken</Link> for flere oplysninger.</>,
        ],
      },
    ],
  },
};

export default function LegalPage({ page }) {
  const data = PAGES[page];

  return (
    <>
      <Seo title={data.title} description={data.intro} path={data.path} />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8 sm:py-16">
        <p className="font-mono text-xs uppercase tracking-widest text-brand-fresh">SpørgMig</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink sm:text-5xl">{data.heading}</h1>
        <p className="mt-5 max-w-3xl text-base leading-7 text-brand-body sm:text-lg">{data.intro}</p>
        <p className="mt-3 text-xs text-brand-muted">Senest opdateret {UPDATED}</p>

        <div className="mt-10 space-y-4">
          {data.sections.map((section) => (
            <section key={section.title} className="rounded-2xl border border-brand-line bg-white p-6 sm:p-8">
              <h2 className="text-xl font-semibold text-brand-ink">{section.title}</h2>
              {section.paragraphs?.map((paragraph, index) => (
                <p key={index} className="mt-3 leading-7 text-brand-body">{paragraph}</p>
              ))}
              {section.bullets && (
                <ul className="mt-3 list-disc space-y-2 pl-5 leading-7 text-brand-body">
                  {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                </ul>
              )}
            </section>
          ))}
        </div>

        {page !== "contact" && (
          <p className="mt-8 text-sm text-brand-body">
            Har du spørgsmål? <Link to="/kontakt" className="font-semibold text-brand-fresh underline underline-offset-4">Kontakt SpørgMig</Link>.
          </p>
        )}
      </main>
    </>
  );
}

function Mail() {
  return <a className="font-semibold text-brand-fresh underline underline-offset-4" href="mailto:support@spoergmig.dk">support@spoergmig.dk</a>;
}

function External({ href, children }) {
  return <a className="font-semibold text-brand-fresh underline underline-offset-4" href={href} target="_blank" rel="noreferrer">{children}</a>;
}

